/**
 * ImportHistorySection Component
 *
 * Displays the history of CSV file imports with status and statistics.
 */

import { useState, useEffect, useCallback } from 'react';
import type ApiAccountingCsvImportLog from '@/types/serializers/ApiAccountingCsvImportLog';

/**
 * Props for ImportHistorySection
 */
interface ImportHistorySectionProps {
  /** New imports to prepend to the list (from parent component) */
  newImports?: ApiAccountingCsvImportLog[];
}

/**
 * Format a relative time string from an ISO date
 */
function formatRelativeTime(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

/**
 * Get status badge styling based on import result
 */
function getStatusBadge(log: ApiAccountingCsvImportLog): { text: string; className: string } {
  if (log.hasErrors) {
    return {
      text: `${log.errorCount} error${log.errorCount !== 1 ? 's' : ''}`,
      className: 'bg-red-100 text-red-800',
    };
  }
  if (log.allDuplicates) {
    return {
      text: 'All duplicates',
      className: 'bg-gray-100 text-gray-700',
    };
  }
  if (log.importedCount > 0) {
    return {
      text: `${log.importedCount} imported`,
      className: 'bg-green-100 text-green-800',
    };
  }
  return {
    text: 'No changes',
    className: 'bg-gray-100 text-gray-600',
  };
}

/**
 * ImportHistorySection displays a list of previous CSV imports.
 */
export function ImportHistorySection({ newImports = [] }: ImportHistorySectionProps) {
  const [imports, setImports] = useState<ApiAccountingCsvImportLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch import history on mount
  const fetchHistory = useCallback(async () => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const response = await fetch('/api/accounting/csv_import/history', {
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      });
      const data = await response.json();
      if (data.success) {
        setImports(data.imports || []);
      } else {
        setError(data.error || 'Failed to load history');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Merge new imports from props
  useEffect(() => {
    if (newImports.length > 0) {
      setImports((prev) => {
        // Filter out any existing entries with the same ID
        const existingIds = new Set(prev.map((i) => i.id));
        const uniqueNewImports = newImports.filter((i) => !existingIds.has(i.id));
        return [...uniqueNewImports, ...prev];
      });
    }
  }, [newImports]);

  // Display limit
  const displayLimit = isExpanded ? imports.length : 5;
  const visibleImports = imports.slice(0, displayLimit);
  const hasMore = imports.length > 5;

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Import History</h3>
        </div>
        <div className="px-6 py-8 text-center text-gray-500">
          <svg className="w-6 h-6 animate-spin mx-auto mb-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Loading history...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Import History</h3>
        </div>
        <div className="px-6 py-8 text-center">
          <div className="text-red-600 mb-2">Failed to load history</div>
          <p className="text-sm text-gray-500">{error}</p>
          <button
            onClick={() => {
              setError(null);
              setIsLoading(true);
              fetchHistory();
            }}
            className="mt-3 text-sm text-blue-600 hover:text-blue-700"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (imports.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Import History</h3>
        </div>
        <div className="px-6 py-8 text-center">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-500">No imports yet</p>
          <p className="text-sm text-gray-400 mt-1">Import a CSV file to see history here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Import History</h3>
        <span className="text-sm text-gray-500">{imports.length} imports</span>
      </div>

      {/* List */}
      <div className="divide-y divide-gray-100">
        {visibleImports.map((log) => {
          const status = getStatusBadge(log);
          return (
            <div key={log.id} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50">
              <div className="flex items-center gap-3 min-w-0">
                {/* Source Icon */}
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>

                {/* File Info */}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 truncate" title={log.filename}>
                      {log.filename}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${status.className}`}>
                      {status.text}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 flex items-center gap-2">
                    <span>{log.sourceLabel}</span>
                    <span className="text-gray-300">|</span>
                    <span>{formatRelativeTime(log.importedAt)}</span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="flex-shrink-0 text-right text-sm">
                <div className="text-gray-700">
                  {log.totalProcessed} processed
                </div>
                {log.skippedCount > 0 && (
                  <div className="text-gray-500 text-xs">
                    {log.skippedCount} skipped
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Show More/Less */}
      {hasMore && (
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 text-center">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {isExpanded ? 'Show less' : `Show ${imports.length - 5} more`}
          </button>
        </div>
      )}
    </div>
  );
}

export default ImportHistorySection;
